import React from 'react';
import { View, Text, StyleSheet, Pressable } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Colors } from '@constants/colors';
import { Spacing, Radius } from '@constants/spacing';
import { Typography } from '@constants/typography';

interface MarketMoverCardProps {
  symbol: string;
  name: string;
  price: number;
  changePercent: number;
  rank: number;
  onPress: () => void;
  isInWatchlist?: boolean;
  onWatchlistToggle?: () => void;
}

export const MarketMoverCard: React.FC<MarketMoverCardProps> = ({
  symbol,
  name,
  price,
  changePercent,
  rank,
  onPress,
  isInWatchlist,
  onWatchlistToggle,
}) => {
  const isGainer = changePercent > 0;
  const changeColor = isGainer ? Colors.market.profit : Colors.market.loss;
  const icon = isGainer ? 'trending-up' : 'trending-down';
  const priceLabel = `$${price.toFixed(2)}`;
  const deltaLabel = `${changePercent > 0 ? '+' : ''}${changePercent.toFixed(2)}%`;

  return (
    <Pressable onPress={onPress} style={styles.card} accessibilityLabel={`${symbol} ${deltaLabel}`}>
      <View style={styles.leftSection}>
        <View style={[styles.rankBadge, { borderColor: changeColor }]}>
          <Text style={[styles.rankText, { color: changeColor }]}>#{rank}</Text>
        </View>
        <View style={styles.infoSection}>
          <Text style={styles.symbol}>{symbol}</Text>
          <Text style={styles.name} numberOfLines={1}>
            {name}
          </Text>
        </View>
      </View>

      <View style={styles.rightSection}>
        <Text style={styles.price}>{priceLabel}</Text>
        <View style={styles.changeContainer}>
          <Ionicons name={icon} size={14} color={changeColor} />
          <Text style={[styles.change, { color: changeColor }]}>{deltaLabel}</Text>
        </View>
      </View>

      {onWatchlistToggle && (
        <Pressable
          onPress={event => {
            event.stopPropagation();
            onWatchlistToggle();
          }}
          style={styles.starButton}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
          accessibilityLabel="Toggle watchlist"
        >
          <Ionicons
            name={isInWatchlist ? 'star' : 'star-outline'}
            size={18}
            color={isInWatchlist ? Colors.ai.accent : Colors.text.tertiary}
            style={{ opacity: isInWatchlist ? 1 : 0.6 }}
          />
        </Pressable>
      )}
    </Pressable>
  );
};

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: Colors.surface.base,
    borderRadius: Radius.md,
    borderWidth: 1,
    borderColor: Colors.border.subtle,
    paddingVertical: Spacing.md,
    paddingHorizontal: Spacing.lg,
    marginBottom: Spacing.sm,
  },
  leftSection: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    marginRight: Spacing.md,
  },
  rankBadge: {
    width: 36,
    height: 36,
    borderRadius: Radius.md,
    borderWidth: 1,
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: Spacing.md,
  },
  rankText: {
    ...Typography.caption,
    fontWeight: '700',
  },
  infoSection: {
    flex: 1,
  },
  symbol: {
    ...Typography.subtitle,
    color: Colors.text.primary,
    marginBottom: 2,
  },
  name: {
    ...Typography.bodySmall,
    color: Colors.text.tertiary,
  },
  rightSection: {
    alignItems: 'flex-end',
  },
  price: {
    ...Typography.body,
    fontWeight: '600',
    color: Colors.text.primary,
    marginBottom: 2,
  },
  changeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  change: {
    ...Typography.bodySmall,
    fontWeight: '600',
    marginLeft: Spacing.xs,
  },
  starButton: {
    padding: Spacing.xs,
    marginLeft: Spacing.sm,
  },
});
